import React, { useEffect, useRef, useState } from 'react';
import { useId, useInteractOutside } from 'react-aria';
import {
  Button, Dialog, OverlayArrow, Popover, type OverlayArrowRenderProps,
} from 'react-aria-components';
import customIcons from '../../utils/icons';

export const InfoPopoverPlacementValues = {
  BottomStart: 'bottom start',
  Right: 'right',
} as const;

export type InfoPopoverPlacement = typeof InfoPopoverPlacementValues[keyof typeof InfoPopoverPlacementValues];

export interface InfoPopoverProps {
  label: string,
  children: React.ReactNode,
  placement?: InfoPopoverPlacement,
}

const closeDelayMs = 300; // to give enough time for the pointer to move to a link inside the bubble

const buttonClasses = 'flex items-center justify-center size-6 bg-transparent text-base text-piptext-default cursor-pointer '
  + 'rounded-sm';

// Draws the arrow for the side the bubble opened on. Each direction is its own SVG because rotating one SVG with CSS
// leaves a gap: react-aria reserves space for the arrow using its size before the rotation
function Arrow({ placement }: Readonly<OverlayArrowRenderProps>) {
  if (placement === 'left' || placement === 'right') {
    return (
      <svg aria-hidden="true" width={9} height={16} viewBox="0 0 9 16" className="fill-pipbg-subtle">
        <path d={placement === 'right' ? 'M9 0 L0 8 L9 16' : 'M0 0 L9 8 L0 16'} />
      </svg>
    );
  }
  return (
    <svg aria-hidden="true" width={16} height={9} viewBox="0 0 16 9" className="fill-pipbg-subtle">
      <path d={placement === 'bottom' ? 'M0 9 L8 0 L16 9' : 'M0 0 L8 9 L16 0'} />
    </svg>
  );
}

// Info icon that opens a bubble like the grey Tooltip, for content with links or buttons
export default function InfoPopover({
  label, children, placement = InfoPopoverPlacementValues.BottomStart,
}: Readonly<InfoPopoverProps>) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLElement>(null);
  const closeTimer = useRef<number|undefined>(undefined);
  const dialogId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);

  const cancelClose = () => window.clearTimeout(closeTimer.current);
  useEffect(() => cancelClose, []);

  const close = () => {
    cancelClose();
    setIsOpen(false);
    setIsPinned(false);
  };

  const scheduleClose = () => {
    cancelClose();
    if (!isPinned) {
      closeTimer.current = window.setTimeout(close, closeDelayMs);
    }
  };

  const openOnHover = () => {
    cancelClose();
    setIsOpen(true);
  };

  // The trigger handles its own presses, so focus or a press moving to it mustn't close the bubble first
  const isOutsideTrigger = (element: Element) => !triggerRef.current?.contains(element);

  // A non-modal popover ignores presses outside it, so close it here, e.g. when a touch user taps elsewhere
  useInteractOutside({
    ref: popoverRef,
    isDisabled: !isOpen,
    onInteractOutside: (event) => {
      if (event.target instanceof Element && isOutsideTrigger(event.target)) {
        close();
      }
    },
  });

  // A press pins a bubble that hover opened, rather than closing it under the pointer
  const togglePinned = () => {
    if (isOpen && isPinned) {
      close();
      return;
    }
    cancelClose();
    setIsOpen(true);
    setIsPinned(true);
  };

  return (
    <>
      <Button
        ref={triggerRef}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={isOpen ? dialogId : undefined}
        onPress={togglePinned}
        onHoverStart={openOnHover}
        onHoverEnd={scheduleClose}
        className={buttonClasses}
      >
        {customIcons.infoIcon}
      </Button>
      <Popover
        ref={popoverRef}
        triggerRef={triggerRef}
        isOpen={isOpen}
        onOpenChange={(isNextOpen) => !isNextOpen && close()}
        shouldCloseOnInteractOutside={isOutsideTrigger}
        isNonModal
        placement={placement}
        offset={9}
        className="tooltip-grey max-w-[501px] rounded cursor-auto"
      >
        <OverlayArrow>
          {({ placement: arrowPlacement }) => <Arrow placement={arrowPlacement} />}
        </OverlayArrow>
        <Dialog id={dialogId} aria-label={label} className="outline-none">
          <div onPointerEnter={cancelClose} onPointerLeave={scheduleClose}>
            {children}
          </div>
        </Dialog>
      </Popover>
    </>
  );
}
